/**
 * measurementModel — the pure calculation core behind daily measurements.
 *
 * A measurement is always taken against a *section* of work already recorded
 * in the Visual Progress Tracker (a progressSegments doc). The section gives
 * the length; the measurement item gives the rule and the properties (width,
 * thickness, layers, density) that turn that length into a quantity.
 *
 *   Section: Pipeline A, excavation, CH 450 → CH 470  (20 m, wet)
 *   Item:    100–250 mm rockfill, unit m³, thickness 400 mm, width 1.2 m
 *   Qty:     20 × 1.2 × 0.4 = 9.6 m³
 *
 * Nothing in this file touches React or Firestore, so the arithmetic that the
 * measurement sheet and the period summary both depend on can be tested on
 * its own.
 */

/* ------------------------------------------------------------------ */
/* Units                                                               */
/* ------------------------------------------------------------------ */

export const UNITS = {
  m3: { key: "m3", label: "m³", decimals: 2 },
  m2: { key: "m2", label: "m²", decimals: 2 },
  m: { key: "m", label: "m", decimals: 2 },
  no: { key: "no", label: "No.", decimals: 0 },
  t: { key: "t", label: "t", decimals: 3 },
  kg: { key: "kg", label: "kg", decimals: 1 },
  h: { key: "h", label: "h", decimals: 1 },
  sum: { key: "sum", label: "Sum", decimals: 2 },
};

export const unitLabel = (unit) => UNITS[unit]?.label || unit || "";

export function formatQty(qty, unit) {
  // null / undefined / "" are "not measured", not zero — Number() would
  // quietly turn all three into 0 and report a quantity that was never taken.
  if (qty === null || qty === undefined || qty === "") return "—";
  const n = Number(qty);
  if (!Number.isFinite(n)) return "—";
  const dp = UNITS[unit]?.decimals ?? 2;
  return n.toLocaleString("en-ZA", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

/* ------------------------------------------------------------------ */
/* Calculation rules                                                   */
/* ------------------------------------------------------------------ */

/**
 * Each rule says which properties it consumes. `fields` drives the form, so a
 * bidim line never asks for a thickness and a pipe line never asks for width.
 */
export const MEASUREMENT_RULES = {
  volume: {
    key: "volume",
    label: "Volume = length × width × thickness",
    fields: ["widthM", "thicknessMm"],
    defaultUnit: "m3",
  },
  area: {
    key: "area",
    label: "Area = length × width",
    fields: ["widthM"],
    defaultUnit: "m2",
  },
  length: {
    key: "length",
    label: "Length = section length × layers",
    fields: ["layers"],
    defaultUnit: "m",
  },
  mass: {
    key: "mass",
    label: "Mass = length × width × thickness × density",
    fields: ["widthM", "thicknessMm", "densityTPerM3"],
    defaultUnit: "t",
  },
  count: {
    key: "count",
    label: "Entered directly",
    fields: ["countQty"],
    defaultUnit: "no",
  },
};

export const ruleFields = (rule) => MEASUREMENT_RULES[rule]?.fields || [];

/* ------------------------------------------------------------------ */
/* Item catalogue                                                      */
/* ------------------------------------------------------------------ */

/**
 * `appliesTo` lists the tracker activities a line is normally measured
 * against. It only orders the picker — any item can be used on any section,
 * because site conditions rarely read the catalogue.
 */
export const MEASUREMENT_ITEMS = [
  // ── Excavation ──
  {
    key: "trench_excavation_dry",
    label: "Trench excavation — dry material",
    group: "Excavation",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 1500 },
    appliesTo: ["excavation"],
    condition: "dry",
    hint: "Thickness here is the excavated depth in mm.",
  },
  {
    key: "trench_excavation_wet",
    label: "Trench excavation — wet material",
    group: "Excavation",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 1500 },
    appliesTo: ["excavation"],
    condition: "wet",
    hint: "Extra-over item for excavation in water. Thickness is the excavated depth in mm.",
  },
  {
    key: "rock_excavation",
    label: "Excavation in hard rock",
    group: "Excavation",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 800 },
    appliesTo: ["excavation"],
  },
  {
    key: "dewatering",
    label: "Dewatering (pump hours)",
    group: "Excavation",
    unit: "h",
    rule: "count",
    defaults: {},
    appliesTo: ["excavation"],
    condition: "wet",
  },
  {
    key: "shoring",
    label: "Trench shoring / shuttering",
    group: "Excavation",
    unit: "m2",
    rule: "area",
    defaults: { widthM: 3.0 },
    appliesTo: ["excavation"],
    hint: "Width is the developed face height of both trench walls.",
  },

  // ── Bedding & fill ──
  {
    key: "rockfill_100_250",
    label: "100–250 mm rockfill",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 400 },
    appliesTo: ["dump_rock", "bedding", "backfill"],
  },
  {
    key: "stone_19mm",
    label: "19 mm crushed stone",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 150 },
    appliesTo: ["bedding", "crusher"],
  },
  {
    key: "stone_13mm",
    label: "13 mm crushed stone",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 100 },
    appliesTo: ["bedding", "crusher"],
  },
  {
    key: "bedding_sand",
    label: "Bedding sand",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 150 },
    appliesTo: ["bedding"],
  },
  {
    key: "g5_material",
    label: "G5 selected fill",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 300 },
    appliesTo: ["backfill"],
  },
  {
    key: "selected_backfill",
    label: "Selected backfill from excavation",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 600 },
    appliesTo: ["backfill"],
  },
  {
    key: "imported_g7",
    label: "Imported G7 backfill",
    group: "Bedding & fill",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 600 },
    appliesTo: ["backfill"],
  },

  // ── Geotextile & wrapping ──
  {
    key: "bidim_a2",
    label: "Bidim A2 geotextile",
    group: "Geotextile",
    unit: "m2",
    rule: "area",
    defaults: { widthM: 2.4 },
    appliesTo: ["bedding", "dump_rock", "backfill"],
    hint: "Width is the developed (unrolled) width wrapping the trench.",
  },
  {
    key: "bidim_a4",
    label: "Bidim A4 geotextile",
    group: "Geotextile",
    unit: "m2",
    rule: "area",
    defaults: { widthM: 2.4 },
    appliesTo: ["bedding", "dump_rock", "backfill"],
    hint: "Width is the developed (unrolled) width wrapping the trench.",
  },
  {
    key: "geogrid",
    label: "Geogrid reinforcement",
    group: "Geotextile",
    unit: "m2",
    rule: "area",
    defaults: { widthM: 1.8 },
    appliesTo: ["backfill"],
  },

  // ── Pipework ──
  {
    key: "pipe_laid",
    label: "Pipe supplied & laid",
    group: "Pipework",
    unit: "m",
    rule: "length",
    defaults: { layers: 1 },
    appliesTo: ["pipe_laying"],
    hint: "Layers = number of parallel pipes in the same trench.",
  },
  {
    key: "pipe_welds",
    label: "Butt welds / joints",
    group: "Pipework",
    unit: "no",
    rule: "count",
    defaults: {},
    appliesTo: ["welding", "pipe_laying"],
  },
  {
    key: "specials_fittings",
    label: "Specials & fittings",
    group: "Pipework",
    unit: "no",
    rule: "count",
    defaults: {},
    appliesTo: ["pipe_laying"],
  },
  {
    key: "marker_tape",
    label: "Warning / marker tape",
    group: "Pipework",
    unit: "m",
    rule: "length",
    defaults: { layers: 1 },
    appliesTo: ["backfill"],
  },

  // ── Concrete ──
  {
    key: "concrete_encasement_qty",
    label: "Concrete encasement",
    group: "Concrete",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 1.1, thicknessMm: 900 },
    appliesTo: ["concrete_encasement"],
  },
  {
    key: "concrete_bedding_qty",
    label: "Concrete bedding / cradle",
    group: "Concrete",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 0.9, thicknessMm: 150 },
    appliesTo: ["concrete_encasement", "bedding"],
  },
  {
    key: "reinforcing_steel",
    label: "Reinforcing steel",
    group: "Concrete",
    unit: "t",
    rule: "count",
    defaults: {},
    appliesTo: ["concrete_encasement", "culverts"],
  },

  // ── Surface works ──
  {
    key: "topsoil_strip",
    label: "Strip & stockpile topsoil",
    group: "Surface works",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 3.0, thicknessMm: 150 },
    appliesTo: ["site_clearance"],
    clearanceSubType: "top_soil",
  },
  {
    key: "premix_removal",
    label: "Break out & cart away premix",
    group: "Surface works",
    unit: "m2",
    rule: "area",
    defaults: { widthM: 1.2 },
    appliesTo: ["site_clearance", "tar_removing"],
    clearanceSubType: "premix",
  },
  {
    key: "tar_cut",
    label: "Saw-cut asphalt edge",
    group: "Surface works",
    unit: "m",
    rule: "length",
    defaults: { layers: 2 },
    appliesTo: ["tar_cutting"],
    hint: "Layers = 2 where both trench edges are cut.",
  },
  {
    key: "premix_reinstatement",
    label: "Premix reinstatement",
    group: "Surface works",
    unit: "m2",
    rule: "area",
    defaults: { widthM: 1.2 },
    appliesTo: ["reinstatement"],
  },
  {
    key: "basecourse_g5",
    label: "G5 basecourse reinstatement",
    group: "Surface works",
    unit: "m3",
    rule: "volume",
    defaults: { widthM: 1.2, thicknessMm: 150 },
    appliesTo: ["reinstatement"],
  },
  {
    key: "kerb_reinstate",
    label: "Kerbing reinstated",
    group: "Surface works",
    unit: "m",
    rule: "length",
    defaults: { layers: 1 },
    appliesTo: ["reinstatement"],
  },

  // ── Traffic accommodation ──
  {
    key: "tmp_accommodation",
    label: "Traffic accommodation (TMP)",
    group: "Traffic accommodation",
    unit: "m",
    rule: "length",
    defaults: { layers: 1 },
    appliesTo: ["tmp"],
  },
  {
    key: "tmp_signage",
    label: "TMP signage & delineators",
    group: "Traffic accommodation",
    unit: "no",
    rule: "count",
    defaults: {},
    appliesTo: ["tmp"],
  },
];

export const MEASUREMENT_ITEM_BY_KEY = Object.fromEntries(
  MEASUREMENT_ITEMS.map((item) => [item.key, item])
);

export const MEASUREMENT_GROUPS = MEASUREMENT_ITEMS.reduce((acc, item) => {
  if (!acc.includes(item.group)) acc.push(item.group);
  return acc;
}, []);

export const CUSTOM_ITEM_KEY = "custom";

/**
 * Items ordered for a given section: everything that matches the section's
 * activity first (and, where the item is condition- or sub-type-specific, the
 * ones that match the section's recorded properties come first of all).
 */
export function itemsForSection(segment) {
  const activity = segment?.activity || "";
  const condition = segment?.excavationCondition || "";
  const subType = segment?.clearanceSubType || "";

  const score = (item) => {
    if (!item.appliesTo?.includes(activity)) return 0;
    if (item.condition && item.condition !== condition) return 1;
    if (item.clearanceSubType && item.clearanceSubType !== subType) return 1;
    if (item.condition || item.clearanceSubType) return 3;
    return 2;
  };

  return [...MEASUREMENT_ITEMS].sort((a, b) => {
    const diff = score(b) - score(a);
    if (diff !== 0) return diff;
    if (a.group !== b.group) return a.group.localeCompare(b.group);
    return a.label.localeCompare(b.label);
  });
}

/** Items the catalogue suggests outright for this section (score > 1). */
export function suggestedItemsForSection(segment) {
  const activity = segment?.activity || "";
  const condition = segment?.excavationCondition || "";
  const subType = segment?.clearanceSubType || "";
  return MEASUREMENT_ITEMS.filter((item) => {
    if (!item.appliesTo?.includes(activity)) return false;
    if (item.condition && item.condition !== condition) return false;
    if (item.clearanceSubType && item.clearanceSubType !== subType) return false;
    return true;
  });
}

/* ------------------------------------------------------------------ */
/* Section length                                                      */
/* ------------------------------------------------------------------ */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Metres of the section a measurement is taken against. */
export function sectionLength(segment) {
  const start = num(segment?.chainageStart);
  const end = num(segment?.chainageEnd);
  if (start === null || end === null) return 0;
  return Math.max(end - start, 0);
}

/* ------------------------------------------------------------------ */
/* Quantity calculation                                                */
/* ------------------------------------------------------------------ */

/**
 * Compute the quantity for one measurement line.
 *
 * `line` carries the rule plus whichever properties that rule consumes.
 * `lengthOverrideM` lets the user measure part of a section (e.g. 12 m of
 * bidim inside a 20 m excavation run) without splitting the section itself.
 *
 * Returns { qty, unit, length, workings } — `workings` is the human-readable
 * sum shown under the input so the site team can check it at a glance.
 */
export function computeMeasurement(line, segment) {
  const rule = MEASUREMENT_RULES[line?.rule] ? line.rule : "count";
  const unit = line?.unit || MEASUREMENT_RULES[rule].defaultUnit;

  const override = num(line?.lengthOverrideM);
  const length = override !== null && override > 0 ? override : sectionLength(segment);

  const widthM = num(line?.widthM) ?? 0;
  const thicknessMm = num(line?.thicknessMm) ?? 0;
  const layers = num(line?.layers) ?? 1;
  const density = num(line?.densityTPerM3) ?? 0;
  const countQty = num(line?.countQty) ?? 0;

  let qty = 0;
  let workings = "";

  switch (rule) {
    case "volume":
      qty = length * widthM * (thicknessMm / 1000);
      workings = `${fmt(length)} m × ${fmt(widthM)} m × ${fmt(thicknessMm / 1000)} m`;
      break;
    case "area":
      qty = length * widthM;
      workings = `${fmt(length)} m × ${fmt(widthM)} m`;
      break;
    case "length":
      qty = length * (layers || 1);
      workings = layers && layers !== 1 ? `${fmt(length)} m × ${fmt(layers)}` : `${fmt(length)} m`;
      break;
    case "mass":
      qty = length * widthM * (thicknessMm / 1000) * density;
      workings = `${fmt(length)} m × ${fmt(widthM)} m × ${fmt(thicknessMm / 1000)} m × ${fmt(density)} t/m³`;
      break;
    case "count":
    default:
      qty = countQty;
      workings = "entered directly";
      break;
  }

  return {
    qty: Math.max(roundTo(qty, 4), 0),
    unit,
    length,
    workings,
    usedOverride: override !== null && override > 0,
  };
}

function fmt(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString("en-ZA", { maximumFractionDigits: 3 });
}

function roundTo(n, dp) {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/**
 * A blank line seeded from a catalogue item, with the item's defaults and any
 * asset-level trench dimensions already filled in.
 */
export function blankMeasurementLine(itemKey, { segment, asset } = {}) {
  const item = MEASUREMENT_ITEM_BY_KEY[itemKey];
  const trenchWidth = num(asset?.trenchWidthM);
  const trenchDepth = num(asset?.trenchDepthM);

  if (!item) {
    return {
      itemKey: CUSTOM_ITEM_KEY,
      itemLabel: "",
      unit: "m3",
      rule: "volume",
      widthM: trenchWidth ?? "",
      thicknessMm: "",
      layers: 1,
      densityTPerM3: "",
      countQty: "",
      lengthOverrideM: "",
      notes: "",
    };
  }

  const defaults = item.defaults || {};
  const fields = ruleFields(item.rule);

  return {
    itemKey: item.key,
    itemLabel: item.label,
    unit: item.unit,
    rule: item.rule,
    // The asset's own trench width beats the catalogue default — the
    // catalogue only guesses, the asset was measured.
    widthM: fields.includes("widthM")
      ? (trenchWidth ?? defaults.widthM ?? "")
      : "",
    thicknessMm: fields.includes("thicknessMm")
      ? (defaults.thicknessMm ?? (trenchDepth ? trenchDepth * 1000 : "") ?? "")
      : "",
    layers: fields.includes("layers") ? (defaults.layers ?? 1) : 1,
    densityTPerM3: fields.includes("densityTPerM3") ? (defaults.densityTPerM3 ?? "") : "",
    countQty: "",
    lengthOverrideM: "",
    notes: "",
    segmentId: segment?.id || "",
  };
}

/* ------------------------------------------------------------------ */
/* Period summary                                                      */
/* ------------------------------------------------------------------ */

/** Inclusive ISO date filter; rows with no date are kept only for "all time". */
export function filterMeasurementsByPeriod(measurements, { from, to } = {}) {
  if (!from && !to) return [...(measurements || [])];
  return (measurements || []).filter((m) => {
    const d = String(m.date || "");
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

/**
 * Roll a set of measurement docs up into the summary measurement sheet:
 * one row per item + unit, with the totals, the sections that contributed and
 * the chainage extent worked.
 */
export function summariseMeasurements(measurements) {
  const byKey = new Map();

  (measurements || []).forEach((m) => {
    const qty = Number(m.qty);
    if (!Number.isFinite(qty)) return;
    const unit = m.unit || "no";
    const key = `${m.itemKey || CUSTOM_ITEM_KEY}::${m.itemLabel || ""}::${unit}`;

    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        itemKey: m.itemKey || CUSTOM_ITEM_KEY,
        label: m.itemLabel || MEASUREMENT_ITEM_BY_KEY[m.itemKey]?.label || "Custom item",
        group: MEASUREMENT_ITEM_BY_KEY[m.itemKey]?.group || "Other",
        unit,
        total: 0,
        entries: 0,
        activities: new Set(),
        dates: new Set(),
        segmentIds: new Set(),
        chainageStart: null,
        chainageEnd: null,
        rows: [],
      });
    }

    const bucket = byKey.get(key);
    bucket.total += qty;
    bucket.entries += 1;
    if (m.segmentActivity) bucket.activities.add(m.segmentActivity);
    if (m.date) bucket.dates.add(m.date);
    if (m.segmentId) bucket.segmentIds.add(m.segmentId);

    const cs = num(m.chainageStart);
    const ce = num(m.chainageEnd);
    if (cs !== null) bucket.chainageStart = bucket.chainageStart === null ? cs : Math.min(bucket.chainageStart, cs);
    if (ce !== null) bucket.chainageEnd = bucket.chainageEnd === null ? ce : Math.max(bucket.chainageEnd, ce);

    bucket.rows.push(m);
  });

  const items = [...byKey.values()]
    .map((b) => ({
      ...b,
      total: roundTo(b.total, 3),
      activities: [...b.activities],
      days: b.dates.size,
      sections: b.segmentIds.size,
      rows: b.rows.sort((a, z) => String(a.date || "").localeCompare(String(z.date || ""))),
    }))
    .sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      return b.total - a.total;
    });

  const groups = [];
  items.forEach((item) => {
    let g = groups.find((x) => x.group === item.group);
    if (!g) {
      g = { group: item.group, items: [] };
      groups.push(g);
    }
    g.items.push(item);
  });

  const totalsByUnit = {};
  items.forEach((item) => {
    totalsByUnit[item.unit] = roundTo((totalsByUnit[item.unit] || 0) + item.total, 3);
  });

  const allDates = [...new Set((measurements || []).map((m) => m.date).filter(Boolean))].sort();

  return {
    items,
    groups,
    totalsByUnit,
    entries: (measurements || []).length,
    days: allDates.length,
    firstDate: allDates[0] || "",
    lastDate: allDates[allDates.length - 1] || "",
  };
}

/** Flat CSV rows for the summary sheet (header included). */
export function measurementSummaryCsv(summary, { assetName = "", periodLabel = "" } = {}) {
  const lines = [
    ["Asset", assetName],
    ["Period", periodLabel],
    [],
    ["Group", "Item", "Unit", "Quantity", "Entries", "Days", "Sections", "From", "To"],
    ...summary.items.map((i) => [
      i.group,
      i.label,
      unitLabel(i.unit),
      formatQty(i.total, i.unit),
      i.entries,
      i.days,
      i.sections,
      i.chainageStart === null ? "" : `CH ${i.chainageStart}`,
      i.chainageEnd === null ? "" : `CH ${i.chainageEnd}`,
    ]),
  ];
  return lines
    .map((r) => (r || []).map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
}
